import { useEffect, useRef, FocusEvent, SyntheticEvent, useState } from 'react';
import { BaseAutocomplete } from '../../../BaseAutocomplete/BaseAutocomplete';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../../context/Keyboard/keyboard.types';
import { useKeyboardIntegration } from '../../../../../context/Keyboard/keyboard';
import { KioskAutocompleteProps } from '../Autocomplete.types';

export const KioskAutocomplete = <T,>({
    id,
    title,
    label,
    ariaLabel,
    labelClass,
    disabled,
    inputValue: defaultInputValue,
    options,
    onOptionSelected,
    getOptionLabel,
    renderOption,
    forceBottomPopper,
    filterOptions,
    clearOnBlur,
    keyboardLayout,
    shiftAfterPeriod,
    shiftAfterSpace,
    shiftAfterAmpersand,
    hideKeyboardOnClickOut,
    capsLock,
    fullWidth,
    seventyFivePercentWidth,
}: KioskAutocompleteProps<T>) => {
    /**
     * A ref to the HTMLInputElement in the MUI TextField.
     */
    const inputRef = useRef<HTMLInputElement>(null);
    /**
     * The focus ref to prevent a stale state when the on-screen keyboard
     * calls any functions that need to know if the field is focused or not.
     */
    const isFocused = useRef<boolean>(false);
    /**
     * The input value ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal value state.
     */
    const valueRef = useRef<string>('');
    /**
     * State of the current input value so that we can enforce a render
     * when the ref updates.
     */
    const [inputValue, setInputValue] = useState<string>(defaultInputValue ?? '');
    /**
     * The caret position ref to prevent a stale state when the on-screen keyboard
     * calls any functions that use the internal caret position state.
     */
    const caretPositionRef = useRef<number | null>(null);
    /**
     * The on-screen keyboard input ref is used to prevent a stale state when we call to update it
     * from keyboard context functions. It is used to prevent the onChange function that recieves a
     * callback from the pattern mask from resetting the caret position incorrectly after we set it
     * using the keyboard context setValue function.
     */
    const wasOSKInput = useRef<boolean>(false);

    useEffect(() => {
        /**
         * Updates the internal caret position when the internal MUI value finishes changing from the
         * pattern mask.
         */
        if (inputRef.current) {
            inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
        }

        /**
         * Since we want to rerender after all the keyboard functionality is finished,
         * we check here for differences between the state and ref before then updating the state
         * if there are differences.
         */
        if (inputValue !== valueRef.current) {
            valueRef.current = inputValue;
        }

        /**
         * We don't want the pattern mask to incorrectly update the caret position,
         * so if the on-screen keyboard was used, this will not trigger.
         */
        if (wasOSKInput.current) {
            wasOSKInput.current = false;
        }
    }, [inputValue]);

    const getValue = (): GetValueReturn => {
        wasOSKInput.current = true;

        if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
            const selectionStart: number = inputRef.current.selectionStart;
            const selectionEnd: number = inputRef.current.selectionEnd;

            return {
                inputValue: valueRef.current,
                caretPositionStart: selectionStart,
                caretPositionEnd: selectionEnd,
            };
        }
        else {
            return {
                inputValue: valueRef.current,
                caretPositionStart: valueRef.current.length,
                caretPositionEnd: valueRef.current.length,
            };
        }
    };

    const setValue = ({ value, caretPosition }: SetValueProps): void => {
        caretPositionRef.current = caretPosition;
        onOptionSelected && onOptionSelected(value);
        setInputValue(value);
    };

    const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
        return {
            value: value,
            caretPosition: caretPosition,
        };
    };

    const onKeyboardInput = (): void => {
        if (inputRef.current && !isFocused.current) {
            inputRef.current.focus();
        }
    };

    const { onFocus, onBlur } = useKeyboardIntegration({
        fieldValue: valueRef.current,
        getValue: getValue,
        setValue: setValue,
        onInputChange: onInputChange,
        onKeyboardInput: onKeyboardInput,
        keyboardLayout: keyboardLayout,
        shiftAfterPeriod: shiftAfterPeriod,
        shiftAfterSpace: shiftAfterSpace,
        shiftAfterAmpersand: shiftAfterAmpersand,
        hideKeyboardOnClickOut: hideKeyboardOnClickOut,
        capsLock: capsLock,
        clearOnBlur: clearOnBlur,
    });

    const handleOnBlur = () => {
        onBlur();
        isFocused.current = false;
    };

    const handleOnChange = (event: SyntheticEvent<Element, Event>, value: string) => {
        if (!wasOSKInput.current) {
            caretPositionRef.current = (event.target as HTMLInputElement).selectionEnd;
        }
        onOptionSelected && onOptionSelected(value);
        setInputValue(value);
    };

    const handleOnFocus = (event: FocusEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        isFocused.current = true;
        onFocus(event);
    };

    return (
        <BaseAutocomplete
            id={id}
            title={title}
            label={label}
            ariaLabel={ariaLabel}
            labelClass={labelClass}
            inputRef={inputRef}
            inputValue={inputValue}
            disabled={disabled}
            options={options}
            filterOptions={filterOptions}
            onOptionSelected={onOptionSelected}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onInputChange={handleOnChange}
            forceBottomPopper={forceBottomPopper}
            capsLock={capsLock}
            fullWidth={fullWidth}
            seventyFivePercentWidth={seventyFivePercentWidth}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            freeSolo={true}
            openOnFocus={true}
        />
    );
};